import React, { createContext, useContext, useState, useCallback, useMemo, useRef, ReactNode } from 'react';
import { Investment } from '../types/Investment';
import { IncidentResponse } from '../types/Incident';
import { ScoreBreakdown, calculateCompleteScore, calculateResponsePreview } from '../scoring/calculator';
import { Scenario, GameScenarioContent, ScenarioIncident } from '../types/ScenarioManagement';
import { IncidentPreventionResult } from '../types/PreventionScore';
import { logger } from '../services/LoggingService';

export type GamePhase = 'scenario-selection' | 'intro' | 'investments' | 'gameplay' | 'scoring' | 'decision-analysis' | 'summary';

interface GameState {
  gamePhase: GamePhase;
  playerName: string;
  companyName: string;
  selectedScenario: Scenario | null;
  availableInvestments: Investment[];
  availableIncidents: ScenarioIncident[];
  selectedInvestments: Investment[];
  incidentResponses: IncidentResponse[];
  preventionResults: IncidentPreventionResult[];
  currentIncidentIndex: number;
  scoreBreakdown: ScoreBreakdown | null;
  lastResponsePreview: Partial<ScoreBreakdown> | null;
}

interface GameContextType {
  gameState: GameState;
  setGamePhase: (phase: GamePhase) => void;
  setPlayerInfo: (name: string, company: string) => void;
  setSelectedScenario: (scenario: Scenario) => void;
  setSelectedInvestments: (investments: Investment[]) => void;
  addIncidentResponse: (response: IncidentResponse) => void;
  addPreventionResult: (result: IncidentPreventionResult) => void;
  setCurrentIncidentIndex: (index: number) => void;
  calculateFinalScore: () => void;
  previewResponseImpact: (optionId: string, incidentId: string, responseTime: number) => Partial<ScoreBreakdown>;
  resetGame: () => void;
}

const initialState: GameState = {
  gamePhase: 'scenario-selection',
  playerName: '',
  companyName: 'TechCorp International',
  selectedScenario: null,
  availableInvestments: [],
  availableIncidents: [],
  selectedInvestments: [],
  incidentResponses: [],
  preventionResults: [],
  currentIncidentIndex: 0,
  scoreBreakdown: null,
  lastResponsePreview: null
};

const GameContext = createContext<GameContextType | undefined>(undefined);

export const useGameContext = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGameContext must be used within a GameProvider');
  }
  return context;
};

interface GameProviderProps {
  children: ReactNode;
}

export const GameProvider: React.FC<GameProviderProps> = ({ children }) => {
  const [gameState, setGameState] = useState<GameState>(initialState);
  const gameStateRef = useRef(gameState);
  gameStateRef.current = gameState;

  const setGamePhase = useCallback((phase: GamePhase) => {
    setGameState(prev => ({ ...prev, gamePhase: phase }));
  }, []);

  const setPlayerInfo = useCallback((name: string, company: string) => {
    setGameState(prev => ({ ...prev, playerName: name, companyName: company }));
  }, []);

  const setSelectedInvestments = useCallback((investments: Investment[]) => {
    setGameState(prev => ({ ...prev, selectedInvestments: investments }));
  }, []);

  const addIncidentResponse = useCallback((response: IncidentResponse) => {
    setGameState(prev => ({
      ...prev,
      incidentResponses: [...prev.incidentResponses, response]
    }));
  }, []);

  const addPreventionResult = useCallback((result: IncidentPreventionResult) => {
    setGameState(prev => ({
      ...prev,
      preventionResults: [...prev.preventionResults, result]
    }));
  }, []);

  const setCurrentIncidentIndex = useCallback((index: number) => {
    setGameState(prev => ({ ...prev, currentIncidentIndex: index }));
  }, []);

  const calculateFinalScore = useCallback(() => {
    const currentState = gameStateRef.current;
    logger.debug('Calculating scores with investment and response data', 'GameContext', {
      investmentCount: currentState.selectedInvestments.length,
      responseCount: currentState.incidentResponses.length
    });

    // Get synergies from the scenario if available
    const scenarioContent = currentState.selectedScenario?.content as GameScenarioContent;
    const synergies = scenarioContent?.synergies;

    const scoreBreakdown = calculateCompleteScore(
      currentState.selectedInvestments,
      currentState.incidentResponses,
      synergies
    );

    logger.info('Score breakdown calculated successfully', 'GameContext', {
      overallScore: scoreBreakdown.overallScore,
      grade: scoreBreakdown.grade
    });
    setGameState(prev => ({ ...prev, scoreBreakdown }));
  }, []);

  const previewResponseImpact = useCallback((optionId: string, incidentId: string, responseTime: number) => {
    const currentState = gameStateRef.current;
    // Get synergies from the scenario if available
    const scenarioContent = currentState.selectedScenario?.content as GameScenarioContent;
    const synergies = scenarioContent?.synergies;

    const preview = calculateResponsePreview(
      optionId,
      incidentId,
      responseTime,
      currentState.selectedInvestments,
      synergies
    );
    setGameState(prev => ({ ...prev, lastResponsePreview: preview }));
    return preview;
  }, []);

  const resetGame = useCallback(() => {
    setGameState(initialState);
  }, []);

  const setSelectedScenario = useCallback(async (scenario: Scenario) => {
    // Extract investments and incidents from the scenario
    const gameContent = scenario.content as GameScenarioContent;
    
    let availableInvestments: Investment[] = [];
    
    // Check if scenario uses new selectedInvestmentIds (preferred) or old investments array
    if (gameContent.selectedInvestmentIds && gameContent.selectedInvestmentIds.length > 0) {
      // Load global investments and filter by selected IDs
      try {
        const { JSONPersistenceService } = await import('../services/JSONPersistenceService');
        const persistence = JSONPersistenceService.getInstance();
        const data = await persistence.loadData();
        
        if (data?.globalInvestments) {
          const globalInvestments = new Map(data.globalInvestments);
          availableInvestments = gameContent.selectedInvestmentIds
            .map(id => globalInvestments.get(id))
            .filter((inv): inv is NonNullable<typeof inv> => Boolean(inv && inv.isEnabled))
            .map(inv => ({
              id: inv.id,
              name: inv.name,
              icon: inv.icon,
              description: inv.description,
              cost: inv.cost,
              impact: inv.impact,
              businessOutcomes: inv.businessOutcomes || [],
              category: inv.category as Investment['category']
            }));
        }
      } catch (error) {
        logger.error('Failed to load global investments', 'GameContext', {
          scenarioId: scenario.id,
          selectedInvestmentIds: gameContent.selectedInvestmentIds
        }, error as Error);
        // Fallback to scenario investments if global loading fails
      }
    }
    
    // Fallback to scenario-specific investments if no global investments found
    if (availableInvestments.length === 0 && gameContent.investments) {
      availableInvestments = gameContent.investments
        .filter(inv => inv.enabled !== false)
        .map(inv => ({
          id: inv.id,
          name: inv.name,
          icon: inv.icon,
          description: inv.description,
          cost: inv.cost,
          impact: inv.impact,
          businessOutcomes: inv.businessOutcomes || [],
          category: inv.category as Investment['category']
        }));
    }
    
    let availableIncidents: ScenarioIncident[] = [];
    
    // Check if scenario uses new selectedIncidentIds (preferred) or old incidents array
    if (gameContent.selectedIncidentIds && gameContent.selectedIncidentIds.length > 0) {
      // Load global incidents and filter by selected IDs
      try {
        const { JSONPersistenceService } = await import('../services/JSONPersistenceService');
        const persistence = JSONPersistenceService.getInstance();
        const data = await persistence.loadData();
        
        if (data?.globalIncidents) {
          const globalIncidents = new Map(data.globalIncidents);
          availableIncidents = gameContent.selectedIncidentIds
            .map(id => globalIncidents.get(id))
            .filter((inc): inc is NonNullable<typeof inc> => Boolean(inc && inc.isEnabled))
            .map(inc => ({
              id: inc.id,
              title: inc.title,
              description: inc.description,
              severity: inc.severity,
              category: inc.category,
              timeLimit: inc.timeLimit,
              potentialImpact: inc.potentialImpact,
              responseOptions: inc.responseOptions,
              enabled: inc.isEnabled,
              order: 0
            }));
        } else {
          // If globalIncidents doesn't exist yet, load from data/incidents.ts
          const { incidents } = await import('../data/incidents');
          availableIncidents = gameContent.selectedIncidentIds
            .map(id => incidents.find(inc => inc.id === id))
            .filter(inc => inc !== undefined)
            .map(inc => ({
              id: inc!.id,
              title: inc!.title,
              description: inc!.description,
              severity: inc!.severity,
              category: inc!.category,
              timeLimit: inc!.timeLimit,
              potentialImpact: inc!.potentialImpact,
              responseOptions: inc!.responseOptions,
              enabled: true,
              order: 0
            }));
        }
      } catch (error) {
        logger.error('Failed to load global incidents', 'GameContext', {
          scenarioId: scenario.id,
          selectedIncidentIds: gameContent.selectedIncidentIds
        }, error as Error);
        // Fallback to scenario incidents if global loading fails
      }
    }
    
    // Fallback to scenario-specific incidents if no global incidents found
    if (availableIncidents.length === 0 && gameContent.incidents) {
      availableIncidents = (gameContent.incidents || [])
        .filter(inc => inc.enabled !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    }
    
    setGameState(prev => ({
      ...prev,
      selectedScenario: scenario,
      availableInvestments,
      availableIncidents,
      companyName: scenario.title || prev.companyName
    }));
  }, []);

  const value = useMemo<GameContextType>(() => ({
    gameState,
    setGamePhase,
    setPlayerInfo,
    setSelectedScenario,
    setSelectedInvestments,
    addIncidentResponse,
    addPreventionResult,
    setCurrentIncidentIndex,
    calculateFinalScore,
    previewResponseImpact,
    resetGame
  }), [
    gameState,
    setGamePhase,
    setPlayerInfo,
    setSelectedScenario,
    setSelectedInvestments,
    addIncidentResponse,
    addPreventionResult,
    setCurrentIncidentIndex,
    calculateFinalScore,
    previewResponseImpact,
    resetGame
  ]);

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};